import { ReactNode, createContext } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from 'redux/user/user.selectors';
import CustomerEditAttributesDrawer from '../customer-add-edit-attributes-drawer/customer-add-edit-attributes-drawer.component';
import { useCustomerAttributesDrawer } from '../customer-add-edit-attributes-drawer/customer-add-edit-attributes-drawer.hooks';
import { useCustomerTagsDrawer } from '../customer-add-edit-tags-drawer/customer-add-edit-tags-drawer.hooks';
import CustomerEditTagsDrawer from '../customer-add-edit-tags-drawer/customer-edit-tags-drawer.component';
import CustomerQuickDetailsDrawer from '../customer-quick-details-drawer/customer-quick-details-drawer.component';
import { useCustomerQuickDetailsDrawer } from '../customer-quick-details-drawer/customer-quick-details-drawer.hooks';
import DeleteEmailTemplateDrawer from '../email-templates-drawers/delete-email-template-drawer/delete-email-template-drawer.component';
import { useDeleteEmailTemplateDrawer } from '../email-templates-drawers/delete-email-template-drawer/delete-email-template-drawer.hooks';
import EmailEditorDrawer from '../email-templates-drawers/email-editor-drawer/email-editor-drawer.component';
import {
  useCreateEmailEditorDrawer,
  useEditEmailEditorDrawer,
} from '../email-templates-drawers/email-editor-drawer/email-editor-drawer.hooks';
import CreateEmailTemplateDrawer from '../email-templates-drawers/email-template-drawer/create-email-template-drawer.component';
import EditEmailTemplateSettingsDrawer from '../email-templates-drawers/email-template-drawer/edit-email-template-settings-drawer.component';
import { useEmailTemplateDrawer } from '../email-templates-drawers/email-template-drawer/email-template-drawer.hooks';
import OrderDetailsDrawer from '../order-details-drawer/order-details-drawer.component';
import { useOrderDetailsDrawer } from '../order-details-drawer/order-details-drawer.hooks';
import PaymentDetailsDrawer from '../transaction-drawers/payment-details-drawer/payment-details-drawer.component';
import { usePaymentDetailsDrawer } from '../transaction-drawers/payment-details-drawer/payment-details-drawer.hooks';
import RefundTransactionDrawer from '../transaction-drawers/refund-transaction-drawer/refund-transaction-drawer.component';
import { useRefundTransactionDrawer } from '../transaction-drawers/refund-transaction-drawer/refund-transaction-drawer.hooks';
import RelatedLocationsDrawer from '../related-locations-drawer/related-locations-drawer.component';
import { useRelatedLocationsDrawer } from '../related-locations-drawer/related-locations-drawer.hooks';
import CreateRelatedLocationDrawer from '../related-locations-drawer/create-related-location-drawer/create-related-location-drawer.component';
import { useCreateRelatedLocationDrawer } from '../related-locations-drawer/create-related-location-drawer/create-related-location-drawer.hooks';

export type GlobalDrawersContextType = {
  globalCustomerQuickDetailsDrawer: ReturnType<
    typeof useCustomerQuickDetailsDrawer
  >;
  globalOrderDetailsDrawer: ReturnType<typeof useOrderDetailsDrawer>;
  globalPaymentDetailsDrawer: ReturnType<typeof usePaymentDetailsDrawer>;
  globalRefundTransactionDrawer: ReturnType<typeof useRefundTransactionDrawer>;
  globalCustomerTagsDrawer: ReturnType<typeof useCustomerTagsDrawer>;
  globalCustomerAttributesDrawer: ReturnType<
    typeof useCustomerAttributesDrawer
  >;
  globalCreateEmailTemplateDrawer: ReturnType<typeof useEmailTemplateDrawer>;
  globalEditEmailTemplateSettingsDrawer: ReturnType<
    typeof useEmailTemplateDrawer
  >;
  globalDeleteEmailTemplateDrawer: ReturnType<
    typeof useDeleteEmailTemplateDrawer
  >;
  globalCreateEmailEditorDrawer: ReturnType<typeof useCreateEmailEditorDrawer>;
  globalEditEmailEditorDrawer: ReturnType<typeof useEditEmailEditorDrawer>;
  globalRelatedLocationsDrawer: ReturnType<typeof useRelatedLocationsDrawer>;
  globalCreateRelatedLocationDrawer: ReturnType<
    typeof useCreateRelatedLocationDrawer
  >;
};

export const GlobalDrawersContext = createContext<
  GlobalDrawersContextType | undefined
>(undefined);

export const GlobalDrawersProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return isAuthenticated ? (
    <GlobalDrawersProviderInner>{children}</GlobalDrawersProviderInner>
  ) : (
    children
  );
};

export const GlobalDrawersProviderInner = ({
  children,
}: {
  children: ReactNode;
}) => {
  const globalCustomerQuickDetailsDrawer = useCustomerQuickDetailsDrawer();
  const globalOrderDetailsDrawer = useOrderDetailsDrawer();
  const globalPaymentDetailsDrawer = usePaymentDetailsDrawer();
  const globalRefundTransactionDrawer = useRefundTransactionDrawer();
  const globalCustomerTagsDrawer = useCustomerTagsDrawer();
  const globalCustomerAttributesDrawer = useCustomerAttributesDrawer();
  const globalCreateEmailTemplateDrawer = useEmailTemplateDrawer();
  const globalEditEmailTemplateSettingsDrawer = useEmailTemplateDrawer();
  const globalDeleteEmailTemplateDrawer = useDeleteEmailTemplateDrawer();
  const globalCreateEmailEditorDrawer = useCreateEmailEditorDrawer();
  const globalEditEmailEditorDrawer = useEditEmailEditorDrawer();
  const globalRelatedLocationsDrawer = useRelatedLocationsDrawer();
  const globalCreateRelatedLocationDrawer = useCreateRelatedLocationDrawer();

  return (
    <GlobalDrawersContext.Provider
      value={{
        globalCustomerQuickDetailsDrawer,
        globalOrderDetailsDrawer,
        globalPaymentDetailsDrawer,
        globalRefundTransactionDrawer,
        globalCustomerTagsDrawer,
        globalCustomerAttributesDrawer,
        globalCreateEmailTemplateDrawer,
        globalEditEmailTemplateSettingsDrawer,
        globalDeleteEmailTemplateDrawer,
        globalCreateEmailEditorDrawer,
        globalEditEmailEditorDrawer,
        globalRelatedLocationsDrawer,
        globalCreateRelatedLocationDrawer,
      }}
    >
      <CustomerQuickDetailsDrawer {...globalCustomerQuickDetailsDrawer} />
      <OrderDetailsDrawer {...globalOrderDetailsDrawer} />
      <PaymentDetailsDrawer {...globalPaymentDetailsDrawer} />
      <RefundTransactionDrawer {...globalRefundTransactionDrawer} />
      <CustomerEditTagsDrawer {...globalCustomerTagsDrawer} />
      <CustomerEditAttributesDrawer {...globalCustomerAttributesDrawer} />
      <CreateEmailTemplateDrawer {...globalCreateEmailTemplateDrawer} />
      <EditEmailTemplateSettingsDrawer
        {...globalEditEmailTemplateSettingsDrawer}
      />
      <EmailEditorDrawer {...globalCreateEmailEditorDrawer} />
      <EmailEditorDrawer {...globalEditEmailEditorDrawer} />
      <DeleteEmailTemplateDrawer {...globalDeleteEmailTemplateDrawer} />
      <RelatedLocationsDrawer {...globalRelatedLocationsDrawer} />
      <CreateRelatedLocationDrawer {...globalCreateRelatedLocationDrawer} />
      {children}
    </GlobalDrawersContext.Provider>
  );
};
